// JWT vencido: Flask-JWT-Extended responde `401 {"msg": "Token has expired"}`
// (sin `code` ni `error`). Las pantallas viejas hacen `fetch` directo y no lo
// distinguen, así que se vigila en un solo lugar: si una petición con el token
// de empresa, de usuario o de admin vuelve 401 por vencimiento, se borra esa
// sesión y se manda al login que le corresponde, con un aviso.
//
// (El promotor persona también está en la lista: su token es `promoter-token`;
// una empresa que opera como promotora usa el token de empresa.)
//
// El staff NO pasa por aquí: `staffFetch` ya trata cualquier 401 como sesión
// terminada y limpia su propio token (ver services/staffApi.js).
const NOTICE_KEY = 'session-expired-notice';

export const SESSION_EXPIRED_MESSAGE = 'Tu sesión venció. Inicia sesión de nuevo para continuar.';

const SESSIONS = [
  { kind: 'empresa', tokenKey: 'empresa-token', clearKeys: ['empresa-token', 'empresa'], loginPath: '/empresa/login' },
  { kind: 'admin', tokenKey: 'admin-token', clearKeys: ['admin-token', 'admin'], loginPath: '/admin/login' },
  { kind: 'user', tokenKey: 'token', clearKeys: ['token', 'user'], loginPath: '/login' },
  { kind: 'promoter', tokenKey: 'promoter-token', clearKeys: ['promoter-token'], loginPath: '/promotor/login' },
];

const safeStorage = (fn) => {
  try {
    return fn();
  } catch (e) {
    return null;
  }
};

// Token Bearer que llevaba la petición (init.headers o el Request).
const bearerOf = (input, init) => {
  let headers = init && init.headers;
  if (!headers && typeof Request !== 'undefined' && input instanceof Request) headers = input.headers;
  if (!headers) return null;
  const value = typeof headers.get === 'function'
    ? headers.get('Authorization')
    : headers.Authorization || headers.authorization;
  const match = /^Bearer\s+(.+)$/i.exec(value || '');
  return match ? match[1] : null;
};

let handling = false;

const expireSession = (session) => {
  if (handling) return;
  handling = true;
  safeStorage(() => {
    session.clearKeys.forEach((key) => localStorage.removeItem(key));
    sessionStorage.setItem(NOTICE_KEY, session.kind);
  });
  if (window.location.pathname !== session.loginPath) {
    window.location.assign(session.loginPath);
  } else {
    handling = false;
  }
};

export const installSessionExpiryHandler = () => {
  if (typeof window === 'undefined' || !window.fetch || window.fetch.__sessionExpiry) return;
  const original = window.fetch.bind(window);

  const wrapped = async (input, init) => {
    const response = await original(input, init);
    if (response.status !== 401) return response;

    const bearer = bearerOf(input, init);
    if (!bearer) return response;
    const session = SESSIONS.find((s) => safeStorage(() => localStorage.getItem(s.tokenKey)) === bearer);
    if (!session) return response;

    try {
      const body = await response.clone().json();
      if (body && !body.code && typeof body.msg === 'string' && /expired/i.test(body.msg)) {
        expireSession(session);
      }
    } catch (e) {
      // cuerpo que no es JSON: no es el 401 de JWT vencido
    }
    return response;
  };
  wrapped.__sessionExpiry = true;
  window.fetch = wrapped;
};

// Para el login de `kind`: el aviso si la sesión de ese tipo se cerró por
// vencimiento. `peek` lo lee sin gastarlo (seguro en el render, incluso en
// StrictMode) y `clear` lo borra (se llama en un efecto).
export const peekSessionNotice = (kind) =>
  (safeStorage(() => sessionStorage.getItem(NOTICE_KEY)) === kind ? SESSION_EXPIRED_MESSAGE : '');

export const clearSessionNotice = (kind) => {
  if (safeStorage(() => sessionStorage.getItem(NOTICE_KEY)) === kind) {
    safeStorage(() => sessionStorage.removeItem(NOTICE_KEY));
  }
};
